import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { employeeService, flexiBenefitsService } from "../../../services/api";
import { DsButton } from "../../../design-system";

function defaultFinancialYear() {
  const d = new Date();
  const y = d.getFullYear();
  const start = d.getMonth() + 1 >= 4 ? y : y - 1;
  return `${start}-${String(start + 1).slice(-2)}`;
}

function formatInr(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(n));
}

/**
 * Admin Opt-ins inside Reimbursement Control Center —
 * list / on-behalf / change / revoke with mandatory remarks (hrOverride).
 */
export default function AdminFlexiOptInsPanel({
  darkMode = false,
  canAct = false,
  financialYear: fyProp = null,
}) {
  const border = darkMode ? "border-slate-700" : "border-slate-200";
  const muted = darkMode ? "text-slate-400" : "text-slate-500";
  const financialYear = fyProp || defaultFinancialYear();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create"); // create | edit
  const [empQuery, setEmpQuery] = useState("");
  const [empHits, setEmpHits] = useState([]);
  const [empSearching, setEmpSearching] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [components, setComponents] = useState([]);
  const [form, setForm] = useState({
    componentCode: "",
    salaryComponentId: "",
    declaredAmount: "",
    structureAnnualMax: "",
    remarks: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await flexiBenefitsService.listDeclarations({ financialYear });
      setRows(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load opt-ins");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [financialYear]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const blob = [
        r.employeeName,
        r.employeeCode,
        r.componentCode,
        r.status,
        r.userEmail,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return blob.includes(q);
    });
  }, [rows, search]);

  const selectedComp = useMemo(
    () =>
      components.find(
        (c) =>
          String(c.code).toUpperCase() === String(form.componentCode || "").toUpperCase()
      ),
    [components, form.componentCode]
  );

  const structureMax = useMemo(() => {
    if (!selectedComp) return Number(form.structureAnnualMax) || 0;
    const monthly =
      selectedComp.structureMonthlyMax != null
        ? Number(selectedComp.structureMonthlyMax)
        : Number(selectedComp.flexiConfig?.monthlyMax) || 0;
    let annual =
      selectedComp.structureAnnualMax != null
        ? Number(selectedComp.structureAnnualMax)
        : Number(selectedComp.flexiConfig?.annualMax) || 0;
    if (!(annual > 0) && monthly > 0) annual = Math.round(monthly * 12 * 100) / 100;
    return annual || Number(form.structureAnnualMax) || 0;
  }, [selectedComp, form.structureAnnualMax]);

  const searchEmployees = async (q) => {
    setEmpQuery(q);
    if (!q || q.trim().length < 2) {
      setEmpHits([]);
      return;
    }
    setEmpSearching(true);
    try {
      const res = await employeeService.getEmployees({
        search: q.trim(),
        limit: 15,
        page: 1,
      });
      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.employees)
          ? res.employees
          : Array.isArray(res)
            ? res
            : [];
      setEmpHits(list);
    } catch {
      setEmpHits([]);
    } finally {
      setEmpSearching(false);
    }
  };

  const pickEmployee = async (emp) => {
    const id = Number(emp.id || emp.employeeId);
    setSelectedEmp({
      id,
      name: emp.name || emp.user?.name || emp.employeeName || "Employee",
      empCode: emp.empCode || emp.employeeCode || "",
    });
    setEmpQuery("");
    setEmpHits([]);
    try {
      const res = await flexiBenefitsService.getEligibleComponents({ employeeId: id });
      setComponents(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "No eligible components for employee");
      setComponents([]);
    }
  };

  const openCreate = () => {
    setFormMode("create");
    setSelectedEmp(null);
    setComponents([]);
    setForm({
      componentCode: "",
      salaryComponentId: "",
      declaredAmount: "",
      structureAnnualMax: "",
      remarks: "",
    });
    setFormOpen(true);
  };

  const openEdit = async (row) => {
    setFormMode("edit");
    setSelectedEmp({
      id: Number(row.employeeId),
      name: row.employeeName || "Employee",
      empCode: row.employeeCode || "",
    });
    setForm({
      componentCode: row.componentCode || "",
      salaryComponentId: row.salaryComponentId ? String(row.salaryComponentId) : "",
      declaredAmount: String(row.declaredAmount ?? ""),
      structureAnnualMax: String(row.structureAnnualMax ?? ""),
      remarks: "",
    });
    setFormOpen(true);
    try {
      const res = await flexiBenefitsService.getEligibleComponents({
        employeeId: row.employeeId,
      });
      setComponents(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setComponents([]);
    }
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!canAct) {
      toast.error("No permission to manage opt-ins");
      return;
    }
    if (!selectedEmp?.id) {
      toast.error("Select an employee");
      return;
    }
    if (!form.componentCode || !form.declaredAmount) {
      toast.error("Component and amount are required");
      return;
    }
    const remarks = String(form.remarks || "").trim();
    if (!remarks) {
      toast.error("Remarks are required for admin opt-in actions");
      return;
    }
    const amount = Number(form.declaredAmount);
    const max = structureMax;
    if (max > 0 && amount > max) {
      toast.error(`Amount cannot exceed structure max (${formatInr(max)})`);
      return;
    }
    setBusy(true);
    try {
      const comp =
        selectedComp ||
        components.find(
          (c) =>
            String(c.code).toUpperCase() === String(form.componentCode).toUpperCase()
        );
      await flexiBenefitsService.upsertDeclaration({
        employeeId: selectedEmp.id,
        financialYear,
        componentCode: form.componentCode,
        salaryComponentId: form.salaryComponentId || comp?.id || null,
        tdsReferenceHeadId: comp?.tdsReferenceHeadId || null,
        structureAnnualMax: max || amount,
        declaredAmount: amount,
        status: "SUBMITTED",
        remarks,
        hrOverride: true,
        flexiConfig: comp?.flexiConfig || comp?.flexiConfigJson || null,
      });
      toast.success(formMode === "edit" ? "Opt-in updated" : "Opt-in saved for employee");
      setFormOpen(false);
      await load();
      window.dispatchEvent(new CustomEvent("admin:flexi-reimbursement-changed"));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const revokeRow = async (row) => {
    if (!canAct) {
      toast.error("No permission to revoke opt-ins");
      return;
    }
    const remarks = window.prompt(
      `Admin revoke for ${row.employeeName || row.employeeCode || "employee"} / ${row.componentCode}.\nEnter mandatory remarks:`
    );
    if (remarks == null) return;
    if (!String(remarks).trim()) {
      toast.error("Remarks are required");
      return;
    }
    if (
      !window.confirm(
        "Revoke this opt-in? If approved/paid claims exist, this is an audited override."
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await flexiBenefitsService.revokeDeclaration({
        employeeId: row.employeeId,
        financialYear: row.financialYear || financialYear,
        componentCode: row.componentCode,
        remarks: String(remarks).trim(),
        hrOverride: true,
      });
      toast.success("Opt-in revoked");
      await load();
      window.dispatchEvent(new CustomEvent("admin:flexi-reimbursement-changed"));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Revoke failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className={`flex h-24 items-center justify-center gap-2 text-sm ${muted}`}>
        <Loader2 className="animate-spin" size={16} /> Loading opt-ins…
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search employee, code, component…"
          className={`min-w-[200px] flex-1 rounded-lg border px-3 py-1.5 text-sm ${border}`}
        />
        {canAct ? (
          <DsButton type="button" size="sm" onClick={openCreate}>
            Opt-in on behalf
          </DsButton>
        ) : null}
        <DsButton type="button" variant="outline" size="sm" onClick={load} disabled={busy}>
          Refresh
        </DsButton>
      </div>

      <div className={`overflow-x-auto rounded-lg border ${border}`}>
        <table className="min-w-full text-sm">
          <thead className="bg-[hsl(var(--ds-muted)/0.35)] text-left text-xs">
            <tr>
              <th className="px-3 py-2">Employee</th>
              <th className="px-3 py-2">Component</th>
              <th className="px-3 py-2 text-right">Declared</th>
              <th className="px-3 py-2 text-right">Structure max</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">FY</th>
              {canAct ? <th className="px-3 py-2 text-right">Actions</th> : null}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={canAct ? 7 : 6} className={`px-3 py-6 text-center text-sm ${muted}`}>
                  No opt-ins for {financialYear}.
                </td>
              </tr>
            ) : (
              filtered.map((r) => (
                <tr key={r.id} className={`border-t ${border}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.employeeName || "—"}</div>
                    <div className={`text-[11px] ${muted}`}>{r.employeeCode || r.employeeId}</div>
                  </td>
                  <td className="px-3 py-2 font-medium">{r.componentCode}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatInr(r.declaredAmount)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatInr(r.structureAnnualMax)}
                  </td>
                  <td className="px-3 py-2">{r.status}</td>
                  <td className="px-3 py-2 text-xs">{r.financialYear}</td>
                  {canAct ? (
                    <td className="px-3 py-2 text-right">
                      <div className="flex flex-wrap justify-end gap-2">
                        {r.status !== "REVOKED" ? (
                          <>
                            <button
                              type="button"
                              className="text-xs text-[hsl(var(--ds-primary))] hover:underline"
                              disabled={busy}
                              onClick={() => openEdit(r)}
                            >
                              Change
                            </button>
                            <button
                              type="button"
                              className="text-xs text-red-600 hover:underline"
                              disabled={busy}
                              onClick={() => revokeRow(r)}
                            >
                              Revoke
                            </button>
                          </>
                        ) : (
                          <span className={`text-[11px] ${muted}`}>—</span>
                        )}
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {formOpen ? (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 p-4">
          <form
            onSubmit={submitForm}
            className={`w-full max-w-lg space-y-3 rounded-xl border p-4 shadow-xl ${
              darkMode ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white"
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <h4 className="text-sm font-semibold">
                  {formMode === "edit" ? "Change opt-in" : "Opt-in on behalf"}
                </h4>
                <p className={`mt-0.5 text-[11px] ${muted}`}>
                  Admin override — remarks required. Bypasses cool-down, max changes, and
                  declare window.
                </p>
              </div>
              <button
                type="button"
                className={`text-xs ${muted}`}
                onClick={() => setFormOpen(false)}
              >
                Close
              </button>
            </div>

            {formMode === "create" ? (
              <div>
                <label className="mb-1 block text-xs font-medium">Employee</label>
                {selectedEmp ? (
                  <div className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm">
                    <span>
                      {selectedEmp.empCode ? `${selectedEmp.empCode} — ` : ""}
                      {selectedEmp.name}
                    </span>
                    <button
                      type="button"
                      className="text-xs text-[hsl(var(--ds-primary))]"
                      onClick={() => {
                        setSelectedEmp(null);
                        setComponents([]);
                        setForm((f) => ({ ...f, componentCode: "", declaredAmount: "" }));
                      }}
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="search"
                      value={empQuery}
                      onChange={(e) => searchEmployees(e.target.value)}
                      placeholder="Search name / emp code…"
                      className={`w-full rounded-lg border px-3 py-2 text-sm ${border}`}
                    />
                    {(empSearching || empHits.length > 0) && (
                      <div
                        className={`absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-lg border shadow-lg ${
                          darkMode ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white"
                        }`}
                      >
                        {empSearching ? (
                          <p className={`px-3 py-2 text-xs ${muted}`}>Searching…</p>
                        ) : (
                          empHits.map((emp) => (
                            <button
                              key={emp.id}
                              type="button"
                              className="block w-full px-3 py-2 text-left text-sm hover:bg-[hsl(var(--ds-muted)/0.35)]"
                              onClick={() => pickEmployee(emp)}
                            >
                              {(emp.empCode || emp.employeeCode || "") +
                                " — " +
                                (emp.name || emp.user?.name || emp.employeeName || "Employee")}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm">
                {selectedEmp?.empCode ? `${selectedEmp.empCode} — ` : ""}
                {selectedEmp?.name}
              </p>
            )}

            <div>
              <label className="mb-1 block text-xs font-medium">Component</label>
              <select
                className={`w-full rounded-lg border px-3 py-2 text-sm ${border}`}
                value={form.componentCode}
                disabled={formMode === "edit"}
                onChange={(e) => {
                  const code = e.target.value;
                  const comp = components.find((c) => c.code === code);
                  setForm((f) => ({
                    ...f,
                    componentCode: code,
                    salaryComponentId: comp?.id ? String(comp.id) : "",
                    declaredAmount: "",
                  }));
                }}
                required
              >
                <option value="">Select…</option>
                {components.map((c) => (
                  <option key={c.id || c.code} value={c.code}>
                    {c.name || c.code}
                  </option>
                ))}
                {formMode === "edit" &&
                form.componentCode &&
                !components.some(
                  (c) =>
                    String(c.code).toUpperCase() ===
                    String(form.componentCode).toUpperCase()
                ) ? (
                  <option value={form.componentCode}>{form.componentCode}</option>
                ) : null}
              </select>
              {form.componentCode ? (
                <p className={`mt-1 text-[11px] ${muted}`}>
                  Structure max (FY): {formatInr(structureMax)}
                </p>
              ) : null}
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium">Declared amount (FY ₹)</label>
              <input
                type="number"
                min={0}
                step="1"
                max={structureMax > 0 ? structureMax : undefined}
                className={`w-full rounded-lg border px-3 py-2 text-sm ${border}`}
                value={form.declaredAmount}
                onChange={(e) => {
                  let v = e.target.value;
                  if (structureMax > 0 && v !== "" && Number(v) > structureMax) {
                    v = String(structureMax);
                  }
                  setForm((f) => ({ ...f, declaredAmount: v }));
                }}
                required
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium">Admin remarks *</label>
              <textarea
                className={`w-full rounded-lg border px-3 py-2 text-sm ${border}`}
                rows={2}
                value={form.remarks}
                onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))}
                placeholder="Reason for override (required)"
                required
              />
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <DsButton
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setFormOpen(false)}
              >
                Cancel
              </DsButton>
              <DsButton type="submit" size="sm" disabled={busy}>
                {busy ? "Saving…" : formMode === "edit" ? "Save change" : "Submit opt-in"}
              </DsButton>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}
